import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Listing, SearchFilters } from '../types';
import { fetchListingsByState, fetchListingsByCity, fetchListingsByFilter, searchListings } from '../services/api';
import ListingCard from '../components/ListingCard';
import FilterSidebar from '../components/FilterSidebar';
import AdUnit from '../components/AdUnit';
import SearchBar from '../components/SearchBar';
import { stateAbbreviations, stateNameToSlug } from '../services/listingHelpers';
import { useIsMobile } from '../hooks/useIsMobile';

const PAGE_SIZE = 20;

const SearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const isMobile = useIsMobile();

  const stateParam = searchParams.get('state') || '';
  const cityParam = searchParams.get('city') || '';
  const queryParam = searchParams.get('q') || '';
  const typeParam = searchParams.get('type') || '';
  const pageParam = parseInt(searchParams.get('page') || '1', 10);

  const [filters, setFilters] = useState<SearchFilters>({
    state: stateParam || undefined,
    city: cityParam || undefined,
    treatmentType: typeParam || undefined,
  });

  const stateSlug = stateParam ? stateNameToSlug(stateAbbreviations[stateParam] || stateParam) : 'florida';

  // Keep filters in sync with URL params so navigation within SearchPage works
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      state: stateParam || undefined,
      city: cityParam || undefined,
      treatmentType: typeParam || undefined,
    }));
  }, [stateParam, cityParam, typeParam]);

  // Client-side filter on the current page of results (does not re-fetch)
  const applyFilters = useCallback((allListings: Listing[]): Listing[] => {
    let result = allListings;

    if (queryParam) {
      const q = queryParam.toLowerCase();
      result = result.filter(l =>
        l.name?.toLowerCase().includes(q) ||
        l.organizationName?.toLowerCase().includes(q) ||
        l.address?.city?.toLowerCase().includes(q) ||
        l.treatmentTypes?.some(t => t.toLowerCase().includes(q)) ||
        l.category?.some(c => c.toLowerCase().includes(q))
      );
    }

    if (filters.treatmentType) {
      result = result.filter(l =>
        l.treatmentTypes?.some(t => t.toLowerCase().includes(filters.treatmentType!.toLowerCase())) ||
        l.listingType?.some(t => t.toLowerCase().includes(filters.treatmentType!.toLowerCase()))
      );
    }

    if (filters.insurance) {
      result = result.filter(l =>
        l.insuranceAccepted?.some(i => i.toLowerCase().includes(filters.insurance!.toLowerCase()))
      );
    }

    if (filters.minRating) {
      result = result.filter(l => (l.ratings?.overall?.value || 0) >= filters.minRating!);
    }

    if (filters.gender) {
      result = result.filter(l =>
        !l.genderRestrictions?.length ||
        l.genderRestrictions.some(g => g.toLowerCase().includes(filters.gender!.toLowerCase()))
      );
    }

    if (filters.environmentalSetting) {
      result = result.filter(l =>
        l.environmentalSetting?.some(e => e.toLowerCase().includes(filters.environmentalSetting!.toLowerCase()))
      );
    }

    result.sort((a, b) => {
      const aVip = a.vipSubscriptions?.some(v => v.status === 'active') ? 1 : 0;
      const bVip = b.vipSubscriptions?.some(v => v.status === 'active') ? 1 : 0;
      if (aVip !== bVip) return bVip - aVip;
      const aRot = a.inAdRotation ? 1 : 0;
      const bRot = b.inAdRotation ? 1 : 0;
      if (aRot !== bRot) return bRot - aRot;
      return (b.ratings?.overall?.value || 0) - (a.ratings?.overall?.value || 0);
    });

    return result;
  }, [queryParam, filters]);

  useEffect(() => {
    const page = pageParam > 0 ? pageParam : 1;
    setCurrentPage(page);
    const load = async () => {
      setLoading(true);
      try {
        let data;
        if (typeParam) {
          // Use server-side filter for treatment type
          const filterParams: Record<string, string> = { treatment_type: typeParam };
          if (stateParam) filterParams.state = stateParam;
          data = await fetchListingsByFilter(filterParams, page, PAGE_SIZE);
        } else if (queryParam) {
          data = await searchListings(queryParam, page, PAGE_SIZE);
        } else if (cityParam && stateParam) {
          data = await fetchListingsByCity(stateParam, cityParam, page, PAGE_SIZE);
        } else if (stateParam) {
          data = await fetchListingsByState(stateParam, page, PAGE_SIZE);
        } else if (filters.state) {
          data = await fetchListingsByState(filters.state, page, PAGE_SIZE);
        } else {
          data = await fetchListingsByState('FL', page, PAGE_SIZE);
        }
        const filtered = applyFilters(data.listings || []);
        setListings(filtered);
        setTotal(data.total || filtered.length);
        setTotalPages(data.totalPages || Math.ceil((data.total || filtered.length) / PAGE_SIZE) || 1);
      } catch {
        setListings([]);
        setTotal(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [stateParam, cityParam, typeParam, queryParam, filters, applyFilters, pageParam]);

  const goToPage = (page: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(page));
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pageTitle = cityParam && stateParam
    ? `Rehabs in ${cityParam}, ${stateAbbreviations[stateParam] || stateParam}`
    : stateParam
    ? `Rehabs in ${stateAbbreviations[stateParam] || stateParam}`
    : typeParam
    ? `${typeParam} Treatment Centers`
    : queryParam
    ? `Search results for "${queryParam}"`
    : 'Browse Rehab Centers';

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 20px' }}>
      <div style={{ marginBottom: '24px' }}>
        <SearchBar initialQuery={queryParam} initialState={stateParam} />
      </div>

      <h1 style={{ fontSize: '28px', margin: '0 0 4px' }}>{pageTitle}</h1>
      <p style={{ color: '#666', margin: '0 0 24px' }}>
        {loading ? 'Loading...' : `${total} total results — page ${currentPage} of ${totalPages}`}
      </p>

      {isMobile && (
        <div style={{ marginBottom: '16px' }}>
          <button
            onClick={() => setFiltersOpen(true)}
            style={{
              width: '100%', padding: '12px', background: '#fff',
              border: '1px solid #e0e0e0', borderRadius: '8px',
              fontSize: '15px', fontWeight: 600, cursor: 'pointer',
              color: '#1a237e', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            }}
          >
            ⚙ Filters
          </button>
          <FilterSidebar filters={filters} onChange={setFilters} isMobile isOpen={filtersOpen} onClose={() => setFiltersOpen(false)} />
        </div>
      )}

      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
        {!isMobile && <FilterSidebar filters={filters} onChange={setFilters} />}

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* VIP Billboard Ad */}
          <div style={{ marginBottom: '16px' }}>
            <AdUnit format="billboard" count={1} stateSlug={stateSlug} city={cityParam} label="Sponsored" />
          </div>

          {/* VIP Generic Ad */}
          <div style={{ marginBottom: '24px' }}>
            <AdUnit format="generic" count={1} stateSlug={stateSlug} city={cityParam} label="Sponsored" />
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#666' }}>Loading listings...</div>
          ) : listings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#666' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
              <h3>No results found</h3>
              <p>Try adjusting your filters or searching a different location.</p>
            </div>
          ) : (
            <>
              {/* Single unified grid — ads are injected as grid cells so they
                  flow naturally with listings instead of breaking the layout. */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))', gap: '20px' }}>
                {(() => {
                  const cells: React.ReactNode[] = [];
                  listings.forEach((l, idx) => {
                    // Inject two sponsored sidebar cells before the 4th listing.
                    // AdUnit returns null when no ad is available, so empty slots
                    // do not create grid holes.
                    if (idx === 3 && listings.length > 3) {
                      cells.push(
                        <AdUnit key="ad-sidebar" format="sidebar" count={1} stateSlug={stateSlug} label="Sponsored" />
                      );
                      cells.push(
                        <AdUnit key="ad-ppcsidebar" format="ppcsidebar" count={1} stateSlug={stateSlug} label="Sponsored" />
                      );
                    }
                    cells.push(<ListingCard key={l.listingId} listing={l} />);
                  });
                  return cells;
                })()}
              </div>

              {/* PPC Billboard — full-width below grid */}
              <div style={{ margin: '24px 0 8px' }}>
                <AdUnit format="ppcbillboard" count={1} stateSlug={stateSlug} label="Sponsored" />
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '40px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage <= 1}
                    style={{
                      padding: '8px 16px', borderRadius: '6px', border: '1px solid #e0e0e0',
                      background: currentPage <= 1 ? '#f5f5f5' : '#fff', cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                      color: currentPage <= 1 ? '#bbb' : '#1a237e', fontWeight: 600
                    }}
                  >← Prev</button>

                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                    // Show pages around current page
                    let page: number;
                    if (totalPages <= 7) {
                      page = i + 1;
                    } else if (currentPage <= 4) {
                      page = i + 1;
                    } else if (currentPage >= totalPages - 3) {
                      page = totalPages - 6 + i;
                    } else {
                      page = currentPage - 3 + i;
                    }
                    return (
                      <button
                        key={page}
                        onClick={() => goToPage(page)}
                        style={{
                          padding: '8px 14px', borderRadius: '6px',
                          border: page === currentPage ? '2px solid #1a237e' : '1px solid #e0e0e0',
                          background: page === currentPage ? '#1a237e' : '#fff',
                          color: page === currentPage ? '#fff' : '#333',
                          fontWeight: page === currentPage ? 700 : 400,
                          cursor: 'pointer'
                        }}
                      >{page}</button>
                    );
                  })}

                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    style={{
                      padding: '8px 16px', borderRadius: '6px', border: '1px solid #e0e0e0',
                      background: currentPage >= totalPages ? '#f5f5f5' : '#fff',
                      cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                      color: currentPage >= totalPages ? '#bbb' : '#1a237e', fontWeight: 600
                    }}
                  >Next →</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchPage;
